import React, { useEffect, useState, useRef } from "react";
import { logResponse } from "../utils/logger";
import iconPay from "../assets/img/icon_pay.svg";
import { fetchGetCustomer, fetchUpdateCustomer } from "../api";
import SubmitButton from "../components/SubmitButton";

const prefectures = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県",
  "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県"
];

const UserInfo = () => {
  const [customer, setCustomer] = useState(null);
  const [prefecture, setPrefecture] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const formRef = useRef();
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [errors, setErrors] = useState({});
  const [municipality, setMunicipality] = useState("");
  const [town, setTown] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [addressDetail, setAddressDetail] = useState("");
  const [addressDetail2, setAddressDetail2] = useState("");  const [customerName, setCustomerName] = useState("");
  const [phoneNumber1, setPhoneNumber1] = useState("");
  const [phoneNumber2, setPhoneNumber2] = useState("");
  const municipalityRef = useRef(null);

  useEffect(() => {
    const access_token = localStorage.getItem("access_token");
    if (!access_token) return;

    fetchGetCustomer(access_token).then((data) => {
      setCustomer(data);
      logResponse("customer:", data);
      setPrefecture(data.prefecture_name || "");
      setMunicipality(data.municipality_name || "");
      setTown(data.town_name || "");
      setPostalCode(data.postal_code || "");
      setAddressNumber(data.address_number || "");
      setAddressDetail(data.address_detail_1 || "");
      setAddressDetail2(data.address_detail_2 || "");
      setCustomerName(data.customer_name || "");
      setPhoneNumber1(data.phone_number_1 || "");
      setPhoneNumber2(data.phone_number_2 || "");
    });
  }, []);

  const resetFormFields = () => {
    if (!customer) return;

    setPrefecture(customer.prefecture_name || "");
    setMunicipality(customer.municipality_name || "");
    setTown(customer.town_name || "");
    setPostalCode(customer.postal_code || "");
    setAddressNumber(customer.address_number || "");
    setAddressDetail(customer.address_detail_1 || "");

    setCustomerName(customer.customer_name || "");
    setPhoneNumber1(customer.phone_number_1 || "");
    setPhoneNumber2(customer.phone_number_2 || "");
  };

  const handlePostalCodeChange = (e) => {
    const value = e.target.value;
    setPostalCode(value);

    const cleanZip = value.replace(/[^0-9]/g, "");
    if (cleanZip.length === 7) {
      fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${cleanZip}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.results && data.results.length > 0) {
            const result = data.results[0];
            setPrefecture(result.address1);
            setMunicipality(result.address2);
            setTown(result.address3);
            setAddressNumber("");
            setAddressDetail("");
            setAddressDetail2("");

            // 7桁目の郵便番号を打ったら市区町村にフォーカスを移動
            setTimeout(() => {
              municipalityRef.current?.focus();
            }, 0);
          }
        })
        .catch(() => {
          setPrefecture("");
          setMunicipality("");
          setTown("");
          setAddressNumber("");
          setAddressDetail("");
          setAddressDetail2(""); 
        });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setMessage("");

    const formData = new FormData(formRef.current);
    const access_token = localStorage.getItem("access_token");
    if (!access_token) return;

    const payload = Object.fromEntries(formData.entries());
    const newErrors = {};

    if (!payload.customer_name || !/^[^\u0020-\u007E\uFF61-\uFF9F]+$/.test(payload.customer_name)) {
      newErrors.customer_name = "氏名は全角で入力してください";
    }

    const isValidPhone = (value) => /^[0-9-]{10,}$/.test(value);

    if (!payload.phone_number_1 || !isValidPhone(payload.phone_number_1)) {
      newErrors.phone_number_1 = "電話番号は10桁以上の半角数字とハイフンのみで入力してください";
    }

    if (payload.phone_number_2 && !isValidPhone(payload.phone_number_2)) {
      newErrors.phone_number_2 = "電話番号2は10桁以上の半角数字とハイフンのみで入力してください";
    }

    if (!payload.postal_code || !/^\d{3}-?\d{4}$/.test(payload.postal_code)) {
      newErrors.postal_code = "郵便番号は半角数字7桁（例：1234567 または 123-4567）で入力してください";
    }

    if (!payload.municipality_name || payload.municipality_name.trim() === "") {
      newErrors.municipality_name = "市区町村は必須です";
    }

    if (!payload.address_number || payload.address_number.trim() === "") {
      newErrors.address_number = "丁目・番地は必須です";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setMessage("");
      setMessageType("error");
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    setErrors({});

    try {
      const result = await fetchUpdateCustomer(access_token, payload);
      logResponse("token:", access_token);
      logResponse("req:", payload);
      logResponse("res:", result);

      setMessage("お客様情報を更新をリクエストしました。反映まで数営業日お待ちください。");
      setMessageType("success");
      setIsEditing(false);
    } catch (err) {
      setMessage("更新に失敗しました。もう一度お試しください");
      setMessageType("error");
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section userinfo-section">
      <h2 className="section-title">
        <i>
          <img src={iconPay} alt="" />
        </i>
        お客様情報
      </h2>

      <div className="userinfo-group">
        <div className="group-wrapper">
          <p className="explain">
            お客様の情報を修正／更新することができます。
            <br />
            変更があった際は、お手数ですが下記より変更をお願い致します。
          </p>

          <div className="form-group">
            <form ref={formRef} onSubmit={handleSubmit}>
              <label>
                <span className="column">氏名</span>
                <input
                  type="text"
                  name="customer_name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  disabled={!isEditing}
                />
                {errors.customer_name && <div className="error">{errors.customer_name}</div>}
              </label>

              <label>
                <span className="column">電話番号</span>
                <input
                  type="tel"
                  name="phone_number_1"
                  value={phoneNumber1}
                  onChange={(e) => setPhoneNumber1(e.target.value)}
                  disabled={!isEditing}
                />
                {errors.phone_number_1 && <div className="error">{errors.phone_number_1}</div>}
              </label>

              <label>
                <span className="column">電話番号2</span>
                <input
                  type="tel"
                  name="phone_number_2"
                  value={phoneNumber2}
                  onChange={(e) => setPhoneNumber2(e.target.value)}
                  disabled={!isEditing}
                />
                {errors.phone_number_2 && <div className="error">{errors.phone_number_2}</div>}
              </label>

              <label>
                <span className="column">住所</span>
              </label>

              <label>
                <span className="column small inline_block mt5">〒</span>
                <input
                  type="text"
                  name="postal_code"
                  value={postalCode}
                  onChange={handlePostalCodeChange}
                  maxLength={8}
                  disabled={!isEditing}
                />
                {errors.postal_code && <div className="error">{errors.postal_code}</div>}
              </label>

              <label>
                <span className="column small">都道府県</span>
                  <select
                    name="prefecture_name"
                    value={prefecture}
                    onChange={(e) => setPrefecture(e.target.value)}
                    disabled={!isEditing}
                  >
                  <option value="">選択してください</option>
                  {prefectures.map((pref) => (
                    <option key={pref} value={pref}>{pref}</option>
                  ))}
                </select>
              </label>

              <label>
                <span className="column small">市区町村</span>
                <input
                  type="text"
                  name="municipality_name"
                  ref={municipalityRef}
                  value={`${municipality}${town}`}
                  onChange={(e) => {
                    setMunicipality(e.target.value);
                    setTown("");
                  }}
                  disabled={!isEditing}
                />
                {errors.municipality_name && <div className="error">{errors.municipality_name}</div>}
              </label>

              <label>
                <span className="column small">丁目・番地</span>
                <input
                  type="text"
                  name="address_number"
                  value={addressNumber}
                  onChange={(e) => setAddressNumber(e.target.value)}
                  disabled={!isEditing}
                />
                {errors.address_number && <div className="error">{errors.address_number}</div>}
              </label>

              <label>
                <span className="column small">マンション・ビル名・号数など</span>
                <input
                  type="text"
                  name="address_detail_1"
                  value={addressDetail}
                  onChange={(e) => setAddressDetail(e.target.value)}
                  disabled={!isEditing}
                />
              </label>
              <label>
              <span className="column small">マンション・ビル名・号数など2</span> 
              <input
                type="text"
                name="address_detail_2" 
                value={addressDetail2}
                onChange={(e) => setAddressDetail2(e.target.value)}
                disabled={!isEditing}
                />
              </label>

              {message && (
                <div
                  style={{
                    marginTop: "20px",
                    color: messageType === "success" ? "green" : "red",
                    fontWeight: "bold",
                  }}
                >
                  {message}
                </div>
              )}
              {!isEditing ? (
                <div className="btn-green">
                  <button
                    type="button"
                    className="btn-green"
                    onClick={() => setIsEditing(true)}
                  >
                    お客様情報変更
                  </button>
                </div>
              ) : (
                <>
                  <div className="btn-green">
                    <SubmitButton
                      label="変更を確定"
                      type="submit"
                      isSubmitting={isSubmitting}
                      wrap={false}
                    />
                  </div>
                  <div className="btn-gray">
                  <SubmitButton
                    label="キャンセル"
                    type="button"
                    onClick={() => {
                      resetFormFields();
                      setIsEditing(false);
                      setErrors({});
                      setMessage("");
                    }}
                    wrap={false}
                  />
                  </div>
                </>
              )}

            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UserInfo;
